import { createSelector } from "@reduxjs/toolkit";
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { normalizeIds } from "../components/utils/normalize";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const selectContainers = (state) => state.containers;
const selectedTerminals = (state) => state.terminals;
const selectedTerminalCalls = (state) => state.terminalCalls;
const selectedTerminalCallByIds = (state) => state.selectedTerminalCalls;
const selectedContainerByIds = (state) => state.selectedContainers;
const selectContainerTypes = (state) => state.containerTypes;
const selectedVoyages = (state) => state.voyages;
const selectedVessels = (state) => state.vessels;

const selectedContainerUploadLoadingTerminalFilter = (state) =>
  state.filter.containerUploadLoadingTerminals;
const selectedContainerUploadUnloadingTerminalFilter = (state) =>
  state.filter.containerUploadUnloadingTerminals;
const selectedContainerLoadingTerminalForTerminalCalls = (state) =>
  state.filter.containerLoadingTerminalCalls;
const selectedContainerUnloadingTerminalForTerminalCalls = (state) =>
  state.filter.containerUnloadingTerminalCalls;
const selectedContainerUploadTypeFilter = (state) =>
  state.filter.containerUploadTypes;
const selectContainerFilter = (state) => state.filter.container;
const selectTerminalCallFilter = (state) => state.filter.terminalCall;
const selectedVoyageFilter = (state) => state.filter.voyage;
const selectedVesselFilter = (state) => state.filter.vessel;
const terminalCallFilter = (state) => state.filter.selectedTerminalCall;

export const selectedFilteredVoyage = createSelector(
  [selectedVoyages, selectedVoyageFilter],
  (voyages, filter) => {
    const filterId = filter?.['@id'] ?? filter;

    let rawVoyage = null;

    if (filterId) {
      rawVoyage = voyages.data.find(v => v['@id'] === filterId) ?? null;
    } else {
      rawVoyage = voyages.data.find(v => {
        const start = v.startDate ? dayjs(v.startDate) : null;
        const end = v.endDate ? dayjs(v.endDate) : null;
        
        return start && end &&
               dayjs().isSameOrAfter(start, 'day') &&
               dayjs().isSameOrBefore(end, 'day')
      });

      if (!rawVoyage && voyages.data.length > 0) {
        rawVoyage = [...voyages.data]
          .sort((a, b) => {
            const aDate = dayjs(a.endDate || a.startDate || 0);
            const bDate = dayjs(b.endDate || b.startDate || 0);
            return bDate.valueOf() - aDate.valueOf();
          })[0];
      }
    }

    if (!rawVoyage) {
      return {
        voyage: null,
        containerIds: [],
        terminalCallIds: [],
      };
    }

    const containerIds = normalizeIds(rawVoyage.containers);
    const terminalCallIds = normalizeIds(rawVoyage.terminalCalls);

    return {
      voyage: rawVoyage,
      containerIds,
      terminalCallIds,
    };
  }
);

export const selectedFilteredVessel = createSelector(
  [selectedVessels, selectedVesselFilter],
  (vessels, selectedVessel) => {
    if (!selectedVessel) {
      return vessels.data[0] ?? null
    }

    return selectedVessel;
  }
);

export const selectedFilteredTerminalCall = createSelector(
  [selectedTerminalCallByIds, terminalCallFilter],
  (terminalCalls, terminalCallFilter) => {

    if (!terminalCallFilter) {
      const data = terminalCalls.data || [];

      if (data.length === 0) {
        return null;
      }

      // The API returns terminal calls in storage order, not voyage order.
      // When no call is pinned, select the chronologically first one so the
      // planner opens on the first (earliest-planned) terminal call.
      return [...data].sort((a, b) => {
        const aDate = a?.planned ? dayjs(a.planned).valueOf() : Number.MAX_SAFE_INTEGER;
        const bDate = b?.planned ? dayjs(b.planned).valueOf() : Number.MAX_SAFE_INTEGER;
        return aDate - bDate;
      })[0];
    }

    return terminalCallFilter;
  }
);

export const selectedFilteredContainers = createSelector(
  [selectedContainerByIds, selectContainerFilter],
  (containers, filter) => {
    let data = containers.data || []

    if (filter.labels?.length) {
      data = data.filter(c =>
        c.labels?.some(label => filter.labels.includes(label['@id']))
      )
    }

    if (filter.types?.length) {
      data = data.filter(c =>
        c.containerType &&
        filter.types.includes(c.containerType['@id'])
      )
    }

    if (filter.loadingTerminals?.length) {
      data = data.filter(c =>
        filter.loadingTerminals.includes(c.loadingTerminal?.['@id']) ||
        filter.loadingTerminals.includes(c.unloadingTerminal?.['@id'])
      )
    }

    return data
  }
)

export const selectFilteredContainers = createSelector(
  [selectContainers, selectContainerFilter, selectedFilteredVoyage],
  (containers, filter, voyagefilter) => {
    let filteredData = containers.data || [];

    if (filter.labels.length) {
      filteredData = filteredData.filter((container) =>
        container.labels?.some((label) =>
          filter.labels.includes(label['@id'])
        )
      );
    }

    if (filter.loadingTerminals?.length) {
      filteredData = filteredData.filter((container) =>
        filter.loadingTerminals.includes(container.loadingTerminal?.["@id"]) ||
        filter.loadingTerminals.includes(container.unloadingTerminal?.["@id"])
      );
    }

    if (voyagefilter.voyage) {
      filteredData = filteredData.filter((container) =>
        container.voyage === voyagefilter.voyage?.['@id']
      );
    }

    return filteredData;
  }
);

export const selectFilteredTerminalCalls = createSelector(
  [selectedTerminalCalls, selectContainerFilter, selectedFilteredVoyage, selectTerminalCallFilter, selectedFilteredVessel],
  (terminalCalls, filter, voyagefilter, terminalCallFilter, selectedFilteredVessel) => {
    let filteredData = terminalCalls.data || [];

    if (terminalCallFilter.labels.length) {
      filteredData = filteredData.filter((terminalCall) =>
        terminalCall.labels?.some((label) =>
          terminalCallFilter.labels.includes(label['@id'])
        )
      );
    }

    if (filter.loadingTerminals?.length) {
      filteredData = filteredData.filter((terminalCall) =>
        filter.loadingTerminals.includes(terminalCall.terminal?.["@id"])
      );
    }

    if (voyagefilter.voyage) {
      filteredData = filteredData.filter((terminalCall) =>
        terminalCall.voyage === voyagefilter.voyage?.['@id']
      );
    }

    if (selectedFilteredVessel && selectedFilteredVessel["@id"]) {
      filteredData = filteredData.filter((terminalCall) =>
        terminalCall.vessel["@id"] === selectedFilteredVessel["@id"]
      );
    }

    return filteredData;
  }
);

const parseAliases = (aliasesField) => {
  let aliases = aliasesField;
  if (typeof aliases === "string") {
    try {
      aliases = JSON.parse(aliases);
    } catch {
      aliases = [];
    }
  }
  if (Array.isArray(aliases)) {
    return aliases.filter((a) => typeof a === "string");
  }
  return [];
};

const filterByAbrevAlias = (data, rawFilters) => {
  const filters = Array.isArray(rawFilters)
    ? rawFilters.map((f) => String(f).toLowerCase())
    : [];
  const result = [];

  for (const item of data) {
    const abbr =
      typeof item.abbreviation === "string"
        ? item.abbreviation.toLowerCase()
        : null;
    const aliases = parseAliases(item.aliases);

    for (const filter of filters) {
      const matches = [];
      if (abbr && abbr.includes(filter)) {
        matches.push(item.abbreviation);
      }
      for (const alias of aliases) {
        if (alias.toLowerCase().includes(filter)) {
          matches.push(alias);
        }
      }
      if (matches.length > 0) {
        result.push({ filter, matches, item });
      }
    }
  }

  return result;
};

const filterTerminalCallByTerminal = (data, rawFilters) => {
  if (!Array.isArray(rawFilters) || rawFilters.length === 0) {
    return [];
  }

  const result = [];

  for (const filter of rawFilters) {
    const matches = data.filter((call) => call.terminal?.["@id"] === filter)

    if (matches.length > 0) {
      const item = data.find((call) => call.terminal?.["@id"] === filter);
      result.push({ filter, matches, item });
    }
  }

  return result;
};

export const selectFilteredContainerUploadLoadingTerminals = createSelector(
  [selectedTerminals, selectedContainerUploadLoadingTerminalFilter],
  (terminals, loadingFilters) =>
    filterByAbrevAlias(terminals.data || [], loadingFilters)
);

export const selectFilteredContainerUploadUnloadingTerminals = createSelector(
  [selectedTerminals, selectedContainerUploadUnloadingTerminalFilter],
  (terminals, unloadingFilters) =>
    filterByAbrevAlias(terminals.data || [], unloadingFilters)
);

export const selectFilteredContainerUploadTypes = createSelector(
  [selectContainerTypes, selectedContainerUploadTypeFilter],
  (containerTypes, typeFilters) =>
    filterByAbrevAlias(containerTypes.data || [], typeFilters)
);

export const selectFilteredTerminalCallsByContainerLoadingTerminal = createSelector(
  [
    selectedTerminalCallByIds,
    selectedContainerLoadingTerminalForTerminalCalls,
  ],
  (terminalCalls, containerLoadingTerminalForTerminalCalls) =>
    filterTerminalCallByTerminal(
      terminalCalls.data || [],
      containerLoadingTerminalForTerminalCalls
    )
);

export const selectFilteredTerminalCallsByContainerUnloadingTerminal = createSelector(
  [
    selectedTerminalCallByIds,
    selectedContainerUnloadingTerminalForTerminalCalls,
  ],
  (terminalCalls, containerLoadingTerminalForTerminalCalls) =>
    filterTerminalCallByTerminal(
      terminalCalls.data || [],
      containerLoadingTerminalForTerminalCalls
    )
);

const normalizeCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const normalizeApiId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;

  return value["@id"] ?? null;
};

const getEntityId = (value) => {
  const apiId = normalizeApiId(value);

  if (apiId) {
    return apiId;
  }

  if (value?.id) {
    return `/api/${value.resourceName || "unknown"}/${value.id}`;
  }

  return null;
};

const resolveById = (value, map) => {
  const id = normalizeApiId(value);

  if (!id) {
    return value;
  }

  return map.get(id) ?? value;
};

const sortTerminalCallsByPlanned = (terminalCalls) => {
  return [...terminalCalls].sort((a, b) => {
    const aDate = a?.planned ? dayjs(a.planned).valueOf() : Number.MAX_SAFE_INTEGER;
    const bDate = b?.planned ? dayjs(b.planned).valueOf() : Number.MAX_SAFE_INTEGER;

    return aDate - bDate;
  });
};

const sortContainersByUid = (containers) => {
  return [...containers].sort((a, b) => {
    return String(a?.uid || "").localeCompare(String(b?.uid || ""));
  });
};

const getTerminalCallNumber = (terminalCall) => {
  if (!terminalCall) {
    return "";
  }

  return (
    terminalCall.reference ||
    terminalCall.code ||
    terminalCall.name ||
    String(terminalCall["@id"] || "")
      .split("/")
      .filter(Boolean)
      .pop() ||
    ""
  );
};

const getTrajectoryKeyFromContainer = (container) => {
  const loadingTerminalCallId =
    normalizeApiId(container?.loadingTerminalCall) ||
    normalizeApiId(container?.loadingTerminal) ||
    "null";

  const unloadingTerminalCallId =
    normalizeApiId(container?.unloadingTerminalCall) ||
    normalizeApiId(container?.unloadingTerminal) ||
    "null";

  return `${loadingTerminalCallId}->${unloadingTerminalCallId}`;
};

const getContainerWeight = (container) => {
  const weight = Number(container?.weight ?? 0);

  return Number.isFinite(weight) ? weight : 0;
};

export const selectVoyageTerminalCallManagerData = createSelector(
  [
    selectedFilteredVoyage,
    selectedTerminalCallByIds,
    selectedContainerByIds,
    terminalCallFilter,
  ],
  (voyageFilter, selectedTerminalCallsState, selectedContainersState, selectedTerminalCallFilterValue) => {
    const voyage = voyageFilter?.voyage ?? null;

    const voyageTerminalCallIds = new Set(voyageFilter?.terminalCallIds || []);
    const voyageContainerIds = new Set(voyageFilter?.containerIds || []);

    const allTerminalCalls = normalizeCollection(selectedTerminalCallsState?.data);
    const allContainers = normalizeCollection(selectedContainersState?.data);

    const terminalCalls = sortTerminalCallsByPlanned(
      allTerminalCalls.filter((terminalCall) => {
        const terminalCallId = normalizeApiId(terminalCall);

        if (voyageTerminalCallIds.size > 0 && terminalCallId) {
          return voyageTerminalCallIds.has(terminalCallId);
        }

        if (voyage?.["@id"]) {
          return normalizeApiId(terminalCall?.voyage) === voyage["@id"];
        }

        return true;
      })
    );

    const terminalCallMap = new Map();

    terminalCalls.forEach((terminalCall) => {
      const id = normalizeApiId(terminalCall);

      if (id) {
        terminalCallMap.set(id, terminalCall);
      }
    });

    const containers = sortContainersByUid(
      allContainers
        .filter((container) => {
          const containerId = normalizeApiId(container);

          if (voyageContainerIds.size > 0 && containerId) {
            return voyageContainerIds.has(containerId);
          }

          if (voyage?.["@id"]) {
            return normalizeApiId(container?.voyage) === voyage["@id"];
          }

          return true;
        })
        .map((container) => {
          return {
            ...container,
            loadingTerminalCall: resolveById(container.loadingTerminalCall, terminalCallMap),
            unloadingTerminalCall: resolveById(container.unloadingTerminalCall, terminalCallMap),
          };
        })
    );

    const groupedTerminalCallsByDateMap = new Map();

    terminalCalls.forEach((terminalCall) => {
      const planned = terminalCall?.planned ? dayjs(terminalCall.planned) : null;
      const key = planned?.isValid() ? planned.format("DD/MM/YYYY") : "No date";

      if (!groupedTerminalCallsByDateMap.has(key)) {
        groupedTerminalCallsByDateMap.set(key, []);
      }

      groupedTerminalCallsByDateMap.get(key).push(terminalCall);
    });

    const groupedTerminalCallsByDate = Array.from(
      groupedTerminalCallsByDateMap.entries()
    )
      .map(([date, calls]) => ({
        date,
        calls: sortTerminalCallsByPlanned(calls),
      }))
      .sort((a, b) => {
        if (a.date === "No date") return 1;
        if (b.date === "No date") return -1;

        return dayjs(a.date, "DD/MM/YYYY").valueOf() - dayjs(b.date, "DD/MM/YYYY").valueOf();
      });

    const terminalCallCountsById = {};

    terminalCalls.forEach((terminalCall) => {
      const terminalCallId = normalizeApiId(terminalCall);

      if (!terminalCallId) {
        return;
      }

      terminalCallCountsById[terminalCallId] = {
        loading: 0,
        unloading: 0,
      };
    });

    containers.forEach((container) => {
      const loadingTerminalCallId = normalizeApiId(container?.loadingTerminalCall);
      const unloadingTerminalCallId = normalizeApiId(container?.unloadingTerminalCall);

      if (loadingTerminalCallId && terminalCallCountsById[loadingTerminalCallId]) {
        terminalCallCountsById[loadingTerminalCallId].loading += 1;
      }

      if (unloadingTerminalCallId && terminalCallCountsById[unloadingTerminalCallId]) {
        terminalCallCountsById[unloadingTerminalCallId].unloading += 1;
      }
    });

    const trajectoryMap = new Map();

    containers.forEach((container) => {
      const key = getTrajectoryKeyFromContainer(container);

      if (!trajectoryMap.has(key)) {
        trajectoryMap.set(key, {
          key,
          containers: [],
          totalWeight: 0,
        });
      }

      const trajectory = trajectoryMap.get(key);

      trajectory.containers.push(container);
      trajectory.totalWeight += getContainerWeight(container);
    });

    const trajectories = Array.from(trajectoryMap.values()).map((trajectory) => {
      const firstContainer = trajectory.containers[0];

      const loadingLabel =
        getTerminalCallNumber(firstContainer?.loadingTerminalCall) ||
        firstContainer?.loadingTerminal?.abbreviation ||
        firstContainer?.loadingTerminal?.name ||
        "—";

      const unloadingLabel =
        getTerminalCallNumber(firstContainer?.unloadingTerminalCall) ||
        firstContainer?.unloadingTerminal?.abbreviation ||
        firstContainer?.unloadingTerminal?.name ||
        "—";

      return {
        ...trajectory,
        title: `${loadingLabel} → ${unloadingLabel}`,
        containers: sortContainersByUid(trajectory.containers),
      };
    });

    const selectedTerminalCall =
      selectedTerminalCallFilterValue ||
      terminalCalls[0] ||
      null;

    return {
      voyage,
      terminalCalls,
      containers,
      groupedTerminalCallsByDate,
      terminalCallCountsById,
      trajectories,
      selectedTerminalCall,
      loading: Boolean(selectedTerminalCallsState?.loading || selectedContainersState?.loading),
    };
  }
);